import { FormArray, FormControl, FormGroup } from "@angular/forms";

import { GovernmentIdType, ICustomCoverLetterContent, IGroupMap, ReportType, TaxIdType, UUID, } from ".";

export interface IEntityMap {
  [entityId: UUID]: IEntity;
}

export type IEntityProgressMap = {
  [entityId: UUID]: number,
}

export interface IEntityAutoCollectExclusionMap {
  [entityId: UUID]: {
    id: string;
  };
}

export interface IEntity {
  id: UUID;
  address: IAddress;
  formationDate: Date | null;
  isFormedAfter2024?: boolean | null;
  legalName: string;
  tradeNames: string[] | null;
  dueDate: Date | null;
  displayDueDate: string | null;
  foreignIdJurisdiction: string | null;
  jurisdictionOfFormation: string | null;
  stateOfFormation: string | null;
  tribeOfFormation: string | null;
  otherTribeOfFormation: string | null;
  taxId: string | null;
  taxIdType: TaxIdType | null;
  status: EntityStatuses;
  fincenStatus: SubmissionStatuses | null;
  lastAcceptedFiling: Date | null;
  internalNotes: string | null;
  assignedTo: IAssignedTo | null;
  progress: number;
  groupId: UUID | null;
  groupName: string | null;
  managingOrgId: UUID | null;
  foreignPooledInvestmentVehicle: boolean;
  ownerRelationships: IOwnerRelationshipReference;
  stripeSubscriptionId: string | null;
  selfBilled: boolean;
  handedOff: boolean; // Indicates if the entity has been handed off to secure file
  consentData: IEntitySubmitConsent | null;
  reportType: ReportType;
  fincenId: string | null;
  optOutOfFincenId: boolean;
  additionalExemptions: string | null;
  isLargeOperatingCompany: boolean | null;
  isInactive: boolean | null;
  userIds: UUID[];
  lastModifiedDate: Date | null;
  lastModifiedBy: UUID;
  inSubscriptionBucket?: boolean;
  progressMissingFields?: IMissingFields[];
  bulkUploaded?: boolean;
  bulkUploadedDate?: Date;
  originalEntity?: IOriginalEntity;
  capTable: ICapTable;
  documentTypes: IDocumentType[];
  type: EntityTypes | null;
  entityOwnership: IEntityOwnershipReference;
  userOwnership: IUserOwnershipReference;
}

/**
* @deprecated since owner re-structuring into their own collection
*/
export interface IDeprecatedEntity {
  address: IAddress;
  formationDate: Date | null;
  isFormedAfter2024?: boolean | null;
  legalName: string;
  tradeNames: string[] | null;
  dueDate: Date | null;
  displayDueDate: string | null;
  foreignIdJurisdiction: string | null;
  jurisdictionOfFormation: string | null;
  stateOfFormation: string | null;
  tribeOfFormation: string | null;
  otherTribeOfFormation: string | null;
  taxId: string | null;
  taxIdType: TaxIdType | null;
  status: EntityStatuses;
  fincenStatus: SubmissionStatuses | null;
  lastAcceptedFiling: Date | null;
  internalNotes: string | null;
  assignedTo: IAssignedTo | null;
  progress: number;
  groupId: UUID;
  foreignPooledInvestmentVehicle: boolean;
  owners: IOwnerReferenceDeprecated;
  ownerRelationships?: IOwnerRelationshipReference;
  stripeSubscriptionId: string | null;
  selfBilled: boolean;
  handedOff: boolean;
  consentData: IEntitySubmitConsent | null;
  reportType: ReportType;
  fincenId: string | null;
  optOutOfFincenId: boolean;
  additionalExemptions: string | null;
  isLargeOperatingCompany: boolean | null;
  isInactive: boolean | null;
  userIds: UUID[];
  managingOrgId: UUID | null;
  id: string;
  groupName: string;
  lastModifiedDate: Date | null;
  inSubscriptionBucket?: boolean;
  progressMissingFields?: IMissingFields[];
  bulkUploaded?: boolean;
  bulkUploadedDate?: Date;
  originalEntity?: IOriginalEntity;
}

export interface IProEntity extends IEntity {
  managingOrgId: UUID;
  groupId: UUID;
  groupName: string;
}

export const isProEntity = (entity: IEntity): entity is IProEntity => {
  return !!entity.managingOrgId;
}

export interface IHandedOffEntity extends IProEntity {
  handedOff: true;
}

export const isHandedOffEntity = (entity: IEntity): entity is IHandedOffEntity => {
  return isProEntity(entity) && entity.handedOff;
}

export interface IFileEntity extends IEntity {
  managingOrgId: null;
  handedOff: false;
  groupId: null;
  groupName: null;
}

export const isFileEntity = (entity: IEntity): entity is IFileEntity => {
  return !entity.managingOrgId && !entity.handedOff;
}

export interface IOriginalEntity {
  legalName: string | null;
  taxId: string | null;
  taxIdType: TaxIdType | null;
}

export type IOwnerRelationshipReference = {
  [ownerId in UUID]?: IOwnerRelationship
};

export type IEntityOwnershipReference = {
  [entityId in UUID]?: IEntityShareRecordReference;
};

export type IUserOwnershipReference = {
  [userId in UUID]?: IUserShareRecordReference;
};

/**
* @deprecated since owner re-structuring into their own collection
*/
export interface IOwnerReferenceDeprecated {
  [ownerId: UUID]: IOwnerDeprecated;
}

export interface IOwnerReference {
  [ownerId: UUID]: IOwner;
}

export interface IMissingFields {
  [key: string]: string[];
}
export interface IObjectProgress {
  objectType: "entity" | "beneficial_owner" | "company_applicant";
  countOfRequiredFields: number;
  countOfFieldsFilled: number;
  percentFilled: number;
  missingFields: IMissingFields;
}
export interface IEntityTotalProgress {
  totalCountOfRequiredFields: number;
  totalCountOfFieldsFilled: number;
  totalPercentFilled: number;
  totalMissingFields: IMissingFields[];
  entityProgress: IObjectProgress;
  ownersProgress: IObjectProgress[];
}

export interface IOwnerRequiredFields {
  isEntity: string[];
  isExemptEntity: string[];
  withFincenId: string[];
  withGovId: string[];
}
export interface IEntityRequiredFields {
  entityFields: string[];
}

export interface IBaseEntityForm {
  legalName: FormControl<string | null>;
  tradeNames: FormArray<FormControl<string | null>>;
  formationDate: FormControl<Date | null>;
  isFormedAfter2024: FormControl<boolean | null>;
  foreignIdJurisdiction: FormControl<string | null>;
  jurisdictionOfFormation: FormControl<string | null>;
  stateOfFormation: FormControl<string | null>;
  tribeOfFormation: FormControl<string | null>;
  otherTribeOfFormation: FormControl<string | null>;
  taxId: FormControl<string | null>;
  taxIdType: FormControl<TaxIdType | null>;
  address: FormGroup<IAddressForm>;
  type: FormControl<EntityTypes | null>;
  // foreignPooledInvestmentVehicle: FormControl<boolean | null>;
}

export interface IEntityForm extends IBaseEntityForm {
  id: FormControl<string | null>;
  groupId: FormControl<string | null>;
  dueDate: FormControl<Date | null>;
  absoluteDueDate: FormControl<Date | null>;
  displayDueDate: FormControl<string | null>;
  status: FormControl<EntityStatuses | null>;
  internalNotes: FormControl<string | null>;
  assignedTo: FormGroup<IAssignedToForm>;
  reportType: FormControl<ReportType | null>;
  fincenId: FormControl<string | null>;
  optOutOfFincenId: FormControl<boolean | null>;
  additionalExemptions: FormControl<string | null>;
  isLargeOperatingCompany: FormControl<boolean | null>;
  isInactive: FormControl<boolean | null>;
  originalEntity: FormGroup<IOriginalEntityForm>;
}

export interface IOriginalEntityForm {
  legalName: FormControl<string | null>;
  taxId: FormControl<string | null>;
  taxIdType: FormControl<TaxIdType | null>;
}

export interface IAddress {
  city: string | null;
  state: string | null;
  streetLine1: string | null;
  streetLine2: string | null;
  zip: string | null;
  country: string | null;
}

export interface IAddressForm {
  city: FormControl<string | null>;
  state: FormControl<string | null>;
  streetLine1: FormControl<string | null>;
  streetLine2: FormControl<string | null>;
  zip: FormControl<string | null>;
  country: FormControl<string | null>;
}

export interface IAssignedTo {
  userId: UUID | null;
  userName: string | null;
}

export interface IAssignedToForm {
  userId: FormControl<string | null>;
  userName: FormControl<string | null>;
}

export type SubmissionStatuses =
  | "submission_accepted"
  | "submission_rejected"
  | "submission_processing"
  | "submission_validation_failed"
  | "submission_validation_passed"
  | "submission_pending";

export interface IFiling {
  reportType: ReportType;
  entitySnapshot: IEntity;
  entityOwnersSnapshot: IOwner[],
  snapshotDate: Date;
  processId: UUID;
  submissionStatus?: SubmissionStatuses;
}

/**
* @deprecated since owner re-structuring into their own collection
*/
export interface IDeprecatedFiling {
  reportType: ReportType;
  entitySnapshot: IDeprecatedEntity;
  entityOwnersSnapshot: undefined;
  snapshotDate: Date;
  processId?: UUID;
  submissionStatus?: SubmissionStatuses;
}

export const isFilingDeprecated = (
  filing: IFiling | IDeprecatedFiling
): filing is IDeprecatedFiling => !filing.entityOwnersSnapshot;

export const isFiling = (
  filing: IFiling | IDeprecatedFiling
): filing is IFiling => !!filing.entityOwnersSnapshot;

export interface IEntityDetails {
  id: UUID;
  orgId: UUID;
  userIds: UUID[];
  auditLog: IAuditLog[];
  filings: (IFiling | IDeprecatedFiling)[];
  jwtToken: string;
  secureFileTransferComplete: boolean;
  ownerIds: UUID[];
}

export interface IEntityQualifiers {
  email: string;
  firstName: string;
  lastName: string;
  requestFincenId?: boolean;
}

export interface IAuditLog {
  id: string;
  date: Date;
  user: string;
  actions: IAuditLogAction[];
  type: "entity" | "owner";
  typeValue: string; // entityId or ownerId
}

export interface IAuditLogAction {
  action: string;
  details: string;
}
export type AuditLogValue =
  | Partial<IEntity>
  | Partial<IOwner>
  | null
  | string
  | string[]
  | IAddress
  | Date
  | undefined;

export interface IAuditLogBuildConfig {
  newValues: Partial<IEntity> | Partial<IOwner> | null;
  oldValues: Partial<IEntity> | Partial<IOwner> | null;
  itemsToNotStoreFullCopy: string[];
  type: "entity" | "owner";
  groups?: IGroupMap;
  parentKey?: string;
  parentKeysToInclude?: string[];
}

export interface IOwnerRelationship {
  id: UUID;
  capTableDetails: IOwnerShareRecordReference;
  ownerType: OwnerType[];
  managementRole: string;
  businessTitle: string;
  boiType: BOIType[];
}

export interface IOwnerShareRecord {
  shareTypeId: UUID;
  ownerId: UUID;
  ownershipAmount: number;
}

export interface IEntityShareRecord {
  shareTypeId: UUID;
  entityId: UUID;
  displayName: string;
  ownershipAmount: number;
}

export interface IUserShareRecord {
  shareTypeId: UUID;
  userId: UUID;
  displayName: string;
  ownershipAmount: number;
}

export type IOwnerShareRecordReference = {
  [shareTypeId in UUID]?: IOwnerShareRecord
}


export type IEntityShareRecordReference = {
  [shareTypeId in UUID]?: IEntityShareRecord
}

export type IUserShareRecordReference = {
  [shareTypeId in UUID]?: IUserShareRecord
}

export interface ICapTable {
  shareTypes: IShareTypeReference;
}

export type IShareTypeReference = {
  [shareTypeId in UUID]?: IShareType
};

export interface IShareType {
  id: UUID;
  order: number;
  name: string;
  units: ShareTypeUnit;
}

export type ShareTypeUnit = "Unit" | "Percent" | "None";

export enum BOIType {
  BENEFICIAL_OWNER = "beneficialOwner",
  COMPANY_APPLICANT = "companyApplicant",
  NONE = "",
}

export enum OwnerType {
  OWNER = "owner",
  ENTITY = "entity",
}

/**
* @deprecated since owner re-structuring into their own collection
*/
export interface IOwnerDeprecated {
  id: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  lastIdCollectionEmailSent: Date | null;
  countOfIdCollectionEmailsSent: number;
  isCompanyApplicant: boolean;
  autoCollectId: boolean;
  lastModifiedDate: Date | null;
  fincenId: string | null;
  // Information about the owner if it's an entity
  isEntity: boolean | null;
  entityLegalName: string | null;
  middleName: string | null;
  suffix: string | null;
  dob: Date | null;
  displayDob: string | null;
  jurisdictionIssuer: string | null;
  issuerState: string | null;
  issuerTribe: string | null;
  issuerOtherTribe: string | null;
  docType: GovernmentIdType | null;
  docIdNum: string | null;
  docUrl: string | null;
  hasFincenId: boolean | null;
  addressType: string | null;
  address: IAddress;
  internalNotes: string | null;
  minor: boolean | null;
  originalAttachmentFileName: string | null;
  // Information about the owner if it's an entity
  isExemptEntity: boolean | null;
  isSignatoryEntity: boolean | null;
}

export interface IOwner {
  id: UUID;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  fincenId: string | null;
  isEntity: boolean;

  lastModifiedDate: Date | null;

  internalNotes: string | null;

  entityIds: UUID[];
  groupIds: UUID[];
  userIds: UUID[];
}

export type IOwnerWithBoiType = IOwner & { boiType: BOIType };
export type IOwnerTuple = [IOwner, BOIType[]];

export type INewOwner<T extends IOwner> = Omit<T, "id">;

export type IPersonOwnerFields = {
  isEntity: false;

  firstName: string;
  lastName: string;
  middleName: string | null;
  suffix: string | null;
  email: string | null;

  hasFincenId: boolean;

  autoCollectId: boolean;
  lastIdCollectionEmailSent: Date | null;
  countOfIdCollectionEmailsSent: number;

  dob: Date | null;
  displayDob: string | null;

  jurisdictionIssuer: string | null;
  issuerState: string | null;
  issuerTribe: string | null;
  issuerOtherTribe: string | null;

  docType: GovernmentIdType | null;
  docIdNum: string | null;
  docUrl: string | null;
  originalAttachmentFileName: string | null;

  addressType: string | null;
  address: IAddress;

  minor: boolean;
}

export type IPersonOwner = IOwner & IPersonOwnerFields;

export const isPersonOwner = (owner: IOwner): owner is IPersonOwner => {
  return !owner.isEntity;
}

export type IEntityOwnerFields = {
  isEntity: true;

  entityLegalName: string;
  fincenId: string | null;

  isExemptEntity: boolean;
  isSignatoryEntity: boolean;
}

export type IEntityOwner = IOwner & IEntityOwnerFields;

export const isEntityOwner = (owner: IOwner): owner is IEntityOwner => {
  return owner.isEntity;
}

export type IPersonOwnerFieldsToDelete = Omit<
  IPersonOwner,
  keyof IEntityOwner
  | "lastIdCollectionEmailSent"
  | "countOfIdCollectionEmailsSent"
  | "docUrl"
  | "originalAttachmentFileName"
>;
export type IEntityOwnerFieldsToDelete = Omit<IEntityOwner, keyof IPersonOwner>;

export type IOwnerFormModel<T extends IOwner> = Omit<T,
  "id" | "isEntity" | "lastModifiedDate" | "internalNotes" | "entityIds" | "groupIds" | "userIds"
>;

export interface IPersonOwnerFormModel extends IOwnerFormModel<
  Omit<IPersonOwner, "originalAttachmentFileName" | "countOfIdCollectionEmailsSent" | "lastIdCollectionEmailSent">
> {
  absoluteDob: Date | null
}

export type IEntityOwnerFormModel = IOwnerFormModel<IEntityOwner>;

export type TypedFormGroup<T> = FormGroup<{
  [K in keyof T]: FormControl<T[K]>;
}>;

export interface IAutoCollectOwner {
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  suffix: string | null;
  email: string | null;
  phone: string | null;
  docIdNum: string | null;
  docType: GovernmentIdType | null;
  dob: Date | null;
  displayDob: string | null;
  jurisdictionIssuer: string | null;
  fincenId: string | null;
  issuerState: string | null;
  lastModifiedDate: Date | null;
  address: IAddress;
}

export interface IAutoCollectOwnerForm {
  name: FormGroup<IAutoCollectOwnerNameForm>;
  email: FormControl<string | null>;
  dob: FormControl<Date | null>;
  absoluteDob: FormControl<Date | null>;
  displayDob: FormControl<string | null>;
  identification: FormGroup<IAutoCollectOwnerIdForm>;
  hasFincenId: FormControl<boolean | null>;
  fincenId: FormControl<string | null>;
  address: FormGroup<IAddressForm>;
}

export interface IAutoCollectOwnerNameForm {
  firstName: FormControl<string | null>;
  middleName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  suffix: FormControl<string | null>;
}

export interface IAutoCollectOwnerIdForm {
  docIdNum: FormControl<string | null>;
  docType: FormControl<GovernmentIdType | null>;
  // dob: FormControl<Date | null>;
  jurisdictionIssuer: FormControl<string | null>;
  issuerState: FormControl<string | null>;
  issuerTribe: FormControl<string | null>;
  issuerOtherTribe: FormControl<string | null>;
}

/**
 * The auto collect form should only show the fields that are
 * required from the user. This is what is returned from the
 * backend to tell the front end which fields to show.
 */
export interface IAutoCollectActiveFields {
  name: boolean;
  email: boolean;
  dob: boolean;
  identification: boolean;
  address: boolean;
  fincenId: boolean;
}

export interface IAutoCollectPrefilledFields {
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  suffix: string | null;
  address: IAddress;
}

export interface IOrgContacts {
  orgPhone: string | null;
  orgEmail: string | null;
}

export interface IOrgContactsForm {
  orgPhone: FormControl<string | null>;
  orgEmail: FormControl<string | null>;
}

export type EntityStatuses =
  | "Set Up"
  | "Ready to Review"
  | "Failed at Review"
  | "Ready to File"
  | "On Hold"
  | "Waiting On Client"
  | "Handoff Email Sent"
  | "Handoff Awaiting Payment"
  | "Handoff Completed"
  | "e-Filing Submitted"
  | "e-Filing Rejected"
  | "e-Filing Accepted"
  | "e-Filing Pending"
  | "Exempt";

export const entityStatuses: { status: EntityStatuses; automated: boolean }[] =
  [
    { status: "Set Up", automated: false },
    { status: "Ready to Review", automated: false },
    { status: "Failed at Review", automated: false },
    { status: "Ready to File", automated: false },
    { status: "Exempt", automated: false },
    { status: "On Hold", automated: false },
    { status: "Waiting On Client", automated: false },
    { status: "Handoff Email Sent", automated: true },
    { status: "Handoff Awaiting Payment", automated: true },
    { status: "Handoff Completed", automated: true },
    { status: "e-Filing Pending", automated: true },
    { status: "e-Filing Submitted", automated: true },
    { status: "e-Filing Rejected", automated: true },
    { status: "e-Filing Accepted", automated: true },
  ];

export interface IEntitySelfBillUpdate {
  id: string;
  selfBilled: boolean;
}

export interface IEntityAutoCollectUpdate {
  id: string;
  autoCollectExcluded: boolean;
}
export interface IGroupAutoCollectUpdate {
  id: string;
  autoCollectExcluded: boolean;
}

export interface IUserAuthorizingEntity {
  consentEmail: string | null;
  consentFirstName: string | null;
  consentLastName: string | null;
}

export interface ISubmitFincenFormSelectOption {
  value: IOwner | IUserAuthorizingEntity;
  displayName: string;
}

export interface IEntityFincenSubmitForm {
  consentEmail: FormControl<string | null>;
  consentLastName: FormControl<string | null>;
  consentFirstName: FormControl<string | null>;
  authorizingEntity: FormControl<ISubmitFincenFormSelectOption["value"] | null>;
  paymentChecked: FormControl<boolean | null>;
  consentChecked: FormControl<boolean | null>;
}

export interface IFileEntityFincenSubmitForm {
  authorizingEntity: FormControl<IOwner | null>;
  consentChecked: FormControl<boolean | null>;
}

export interface IEntitySubmitConsent {
  firstName: string;
  lastName: string;
  email: string;
  timestamp: string;
}

export interface ICoverLetterData {
  companyName: string;
  orgLogo?: string;
  userName: string;
  currentDate: string;
  customCoverLetterContent?: ICustomCoverLetterContent;
}

export interface IUploadAttachment {
  processId: string;
  fileName: string;
  fileData: string;
  accessToken: string;
  contentType: string;
}

export interface IUploadAttachmentResponse {
  response: string;
  fileName: string;
  ownerId: UUID;
}

export interface IBoirFilingResponse {
  processId: string;
  xmlUploadResponse: string;
  attachmentUploadResponses: IUploadAttachmentResponse[];
}

export interface IEntityBoirStatusUpdate {
  status: EntityStatuses;
  fincenStatus: SubmissionStatuses | null;
  fincenId?: string;
}


export interface IDocumentType {
  id: UUID;
  name: string;
  documents: UUID[];
}

export interface ICustomDocumentType {
  id: UUID;
  name: string;
}

export interface IEntityDocument {
  id: UUID;
  name: string;
  createdDate: Date;
  updatedDate: Date;
  effectiveDate: Date | null;
  internalOnly: boolean;
  versions: IEntityDocumentVersion[];
  relatesToEntities: UUID[];
  relatesToOwners: UUID[];
  relatesToGroups: UUID[];
}

export interface IEntityDocumentVersion {
  fileRef: string;
  originalFileName: string;
  uploadedDate: Date;
}

export interface IDocumentRelatesTo {
  groups: UUID[];
  entities: UUID[];
  owners: UUID[];
}

export enum EntityTypes {
  SoleProprietorship = 'SoleProprietorship',
  Partnership = 'Partnership',
  LimitedPartnership = 'LimitedPartnership',
  LimitedLiabilityPartnership = 'LimitedLiabilityPartnership',
  LimitedLiabilityCompany = 'LimitedLiabilityCompany',
  Corporation = 'Corporation',
  SCorporation = 'SCorporation',
  CCorporation = 'CCorporation',
  NonProfit = 'NonProfit',
  ProfessionalCorporation = 'ProfessionalCorporation',
  BenefitCorporation = 'BenefitCorporation',
  Cooperative = 'Cooperative',
  Trust = 'Trust',
  JointVenture = 'JointVenture',
  GovernmentEntity = 'GovernmentEntity',
  Other = 'Other',
}

export type ICustomDocumentTypesMap = {
  [entityType in EntityTypes | "General"]?: ICustomDocumentType[]
}

export const ENTITY_TYPE_LABELS: Record<EntityTypes, string> = {
  [EntityTypes.SoleProprietorship]: 'Sole Proprietorship',
  [EntityTypes.Partnership]: 'Partnership',
  [EntityTypes.LimitedPartnership]: 'Limited Partnership (LP)',
  [EntityTypes.LimitedLiabilityPartnership]: 'Limited Liability Partnership (LLP)',
  [EntityTypes.LimitedLiabilityCompany]: 'Limited Liability Company (LLC)',
  [EntityTypes.Corporation]: 'Corporation',
  [EntityTypes.SCorporation]: 'S-Corporation (S-Corp)',
  [EntityTypes.CCorporation]: 'C-Corporation (C-Corp)',
  [EntityTypes.NonProfit]: 'Nonprofit Organization (501(c))',
  [EntityTypes.Cooperative]: 'Cooperative (Co-op)',
  [EntityTypes.Trust]: 'Trust',
  [EntityTypes.ProfessionalCorporation]: 'Professional Corporation (PC)',
  [EntityTypes.BenefitCorporation]: 'Benefit Corporation (B-Corp)',
  [EntityTypes.JointVenture]: 'Joint Venture',
  [EntityTypes.GovernmentEntity]: 'Government Entity',
  [EntityTypes.Other]: 'Other',
};
